<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import MenuItem from '@/components/menu/common/item.vue';
import SectionWrapper from '@/components/menu/sections/section-wrapper.vue';
import { Contract } from '@/types';
import { useIsNextLayout } from '@/utils/composables/useLayoutVariant';
import { useOrganizationTagRoute } from '@/utils/composables/useOrganizationTagRoute';
import { useManagementContracts } from '@/utils/swr';
import { useStore } from '@/utils/vue';

const { data: managementContracts } = useManagementContracts();

const store = useStore();
const route = useRoute();
const router = useRouter();
const isNextLayout = useIsNextLayout();
const { listRoute: organizationTagListRoute } = useOrganizationTagRoute();
const userDocumentRouteNames = new Set(
  router.options.routes
    // '/' 以下の childrenに定義にしてあるので絞り込む
    .find(r => r.path === '/')
    ?.children?.filter(r => r.path.includes('/admin/user_documents'))
    .map(r => r.name ?? null),
);

const teamInfo = computed(() => store.state.teamInfo.teamInfo);

const showUserDocumentAdminTab = computed(
  () => !!teamInfo.value?.enable_user_document,
);

// メディアの表示設定は新デザインだけの画面（旧デザインはニュースメニューのプルダウン）
const showMediaDisplaySetting = computed(
  () => isNextLayout && !!teamInfo.value?.enable_foreign_lang_media,
);

const isUserDocumentAdminTabSelected = computed(
  () =>
    showUserDocumentAdminTab.value &&
    userDocumentRouteNames.has(route.name ?? null),
);

const isAdminContractsTabSelected = computed(
  () =>
    route.name === 'contracts' ||
    route.name === 'contractMembers' ||
    route.name === 'contractCreditsUsages' ||
    route.name === 'contractCreditsGrants',
);

const isMemberListViewable = ref(true);
const contracts = computed(
  () => managementContracts.value?.management_contracts,
);
watch(
  () => contracts.value,
  (changedContracts: Contract[] | undefined) => {
    isMemberListViewable.value =
      changedContracts !== undefined && changedContracts?.length > 0;
  },
);

const hasUserDocumentNotification = computed(
  () => store.state.userDocumentNotification.hasNotification,
);
</script>
<template>
  <SectionWrapper>
    <div class="item-list">
      <div class="title-icon c-title-bold--s">
        <span class="title">管理</span>
      </div>
      <div class="items">
        <ul class="list-style--none">
          <li>
            <MenuItem
              id="admin_contracts"
              name="契約・クレジット管理"
              :route="{ name: 'contracts' }"
              :is-disabled="!isMemberListViewable"
              :is-selected="isAdminContractsTabSelected"
            ></MenuItem>
          </li>
          <li>
            <MenuItem
              id="admin_members"
              name="メンバー管理"
              :route="{ name: 'members' }"
              :is-disabled="!isMemberListViewable"
            ></MenuItem>
          </li>
          <li>
            <MenuItem
              id="organization_tag_list"
              name="組織タグ管理"
              :route="organizationTagListRoute"
            ></MenuItem>
          </li>
          <li>
            <MenuItem
              id="admin_theme"
              name="テーマ管理"
              :route="{ name: 'themeAdmin' }"
            ></MenuItem>
          </li>
          <li>
            <MenuItem
              id="admin_group"
              name="グループ管理"
              :route="{ name: 'groupAdmin' }"
            ></MenuItem>
          </li>
          <li v-if="showUserDocumentAdminTab">
            <MenuItem
              id="admin_user_documents"
              name="社内情報ストレージ管理"
              :route="{
                name: 'anewsStorageIndex',
              }"
              :is-selected="isUserDocumentAdminTabSelected"
              :has-notification="hasUserDocumentNotification"
            ></MenuItem>
          </li>
          <li>
            <MenuItem
              id="admin_dashboard"
              name="レポート"
              :route="{ name: 'dashboardHome' }"
              :is-external-link="true"
            ></MenuItem>
          </li>
        </ul>
      </div>
    </div>

    <div class="item-list">
      <div class="title-icon c-title-bold--s">
        <span class="title">設定</span>
      </div>
      <div class="items">
        <ul class="list-style--none">
          <li>
            <MenuItem
              id="environment"
              name="環境設定"
              :route="{ name: 'environmentSettings' }"
            ></MenuItem>
          </li>
          <li>
            <MenuItem
              id="ip_address"
              name="IPアドレス制限"
              :route="{ name: 'ipAddressRestrictions' }"
            ></MenuItem>
          </li>
          <li>
            <MenuItem
              id="sso"
              name="SSO認証"
              :route="{ name: 'samlFederationList' }"
            ></MenuItem>
          </li>
          <li v-if="showMediaDisplaySetting">
            <MenuItem
              id="media_display"
              name="メディアの表示設定"
              :route="{ name: 'adminMediaDisplay' }"
            ></MenuItem>
          </li>
        </ul>
      </div>
    </div>
  </SectionWrapper>
</template>
<style lang="scss" scoped>
.note {
  display: flex;
  flex-direction: column;
  padding: 8px;
  box-sizing: border-box;
  width: var(--menu-item-width, 240px);
  color: $color-gray1000;
}
/** memo: 汎化可能  */
.list-style--none {
  list-style: none;
}
.c-title-bold--s {
  padding: 4px;
}
</style>
